import type { Board } from "../board";
import type { Direction } from "../direction";
import { directionFromVector } from "../direction";
import type { PointsToDirection, PostDeserializable } from "../interfaces";
import { deserializeXY } from "../serialization";
import { Mob } from "../mob";
import { ParticleType } from "../particleType";
import type { PhysicalThing } from "../physicalThing";
import { XY } from "../xy";
import { SquidCorpse } from "./squidCorpse";

export class SnakeBody extends Mob implements PointsToDirection, PostDeserializable {
	static key = "SnakeBody";
	private leader: Mob;
	private lastLeaderPosition: XY;
	private _pendingLeaderId: string | null = null;

	constructor(xy: XY, leader: Mob) {
		super(xy);
		this.leader = leader;
		this.lastLeaderPosition = leader.xy;
	}

	getDirection(): Direction | null {
		const delta = new XY(Math.sign(this.leader.xy.x - this.xy.x), Math.sign(this.leader.xy.y - this.xy.y));
		return directionFromVector(delta);
	}

	// Direction is derived from the leader's position — ignore external sets.
	setDirection(_dir: Direction): void {}

	override getBloodParticleType(): ParticleType {
		return ParticleType.BLOOD;
	}

	generateCorpse(): PhysicalThing | null {
		return new SquidCorpse(this.xy);
	}

	processMobAction(_board: Board): void {
		if (!this.lastLeaderPosition.equals(this.leader.xy)) {
			this.xy = this.lastLeaderPosition;
			this.lastLeaderPosition = this.leader.xy;
		}
	}

	getLeader(): Mob {
		return this.leader;
	}

	setLeader(leader: Mob): void {
		this.leader = leader;
		this.lastLeaderPosition = leader.xy;
	}

	postDeserialize(board: Board): void {
		if (this._pendingLeaderId == null) return;
		const leader = board.getPhysicalThings().find((t) => t.id === this._pendingLeaderId);
		if (leader instanceof Mob) {
			this.leader = leader;
		}
		this._pendingLeaderId = null;
	}

	serialize(): Record<string, unknown> {
		return {
			...super.serialize(),
			leaderId: this.leader.id,
			lastLeaderPosition: { x: this.lastLeaderPosition.x, y: this.lastLeaderPosition.y },
		};
	}

	static deserialize(data: any): SnakeBody {
		// Create with a temporary dummy leader; the real leader is linked in postDeserialize
		const dummyLeader = { xy: deserializeXY(data.lastLeaderPosition) } as Mob;
		const m = new SnakeBody(deserializeXY(data.xy), dummyLeader);
		m.restoreMobFields(data);
		m.lastLeaderPosition = deserializeXY(data.lastLeaderPosition);
		m._pendingLeaderId = data.leaderId;
		return m;
	}
}
